/* ============================================
   ChatMediaPanel — Shared files panel
   Shows multimedia, documents, and links
   from a conversation, grouped by month
   ============================================ */
import { useState, useEffect, useCallback, useRef } from 'react'
import {
  IconImage, IconFile, IconExternalLink, IconXCircle,
  IconDownload, IconPaperclip, IconChevronLeft,
} from '@/components/icons/Icons'
import type { Mensaje } from '@/lib/types'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

type MediaTab = 'multimedia' | 'documentos' | 'enlaces'

interface ChatMediaPanelProps {
  conversacionId: number
  messages: Mensaje[]
  onImageClick: (url: string) => void
  onPdfClick: (url: string, name: string) => void
  onClose: () => void
}

function formatMediaDate(dateStr: string): string {
  const d = new Date(dateStr)
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

function formatSize(bytes: number | null): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function monthLabel(dateStr: string): string {
  const d = new Date(dateStr)
  const now = new Date()
  if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()) {
    return 'ESTE MES'
  }
  return d.toLocaleDateString('es-PE', { month: 'long', year: 'numeric' }).toUpperCase()
}

function groupByMonth<T extends { fecha_mensaje: string }>(items: T[]): { label: string; items: T[] }[] {
  const groups: { label: string; items: T[] }[] = []
  let currentLabel = ''

  for (const item of items) {
    const label = monthLabel(item.fecha_mensaje)
    if (label !== currentLabel) {
      groups.push({ label, items: [] })
      currentLabel = label
    }
    groups[groups.length - 1].items.push(item)
  }

  return groups
}

// Extract URLs from text messages
const URL_REGEX = /https?:\/\/[^\s<>"{}|\\^`[\]]+/gi

function extractLinks(messages: Mensaje[]): { url: string; mensaje: Mensaje }[] {
  const links: { url: string; mensaje: Mensaje }[] = []
  const seen = new Set<string>()

  for (const msg of messages) {
    if (msg.tipo !== 'texto' || !msg.contenido) continue
    const matches = msg.contenido.match(URL_REGEX)
    if (matches) {
      for (const url of matches) {
        if (!seen.has(url)) {
          seen.add(url)
          links.push({ url, mensaje: msg })
        }
      }
    }
  }

  return links
}

function fileIcon(mime: string | null, nombre: string | null): string {
  if (mime?.includes('pdf')) return '📄'
  if (mime?.includes('word') || nombre?.endsWith('.doc') || nombre?.endsWith('.docx')) return '📝'
  if (mime?.includes('sheet') || mime?.includes('excel') || nombre?.endsWith('.xls') || nombre?.endsWith('.xlsx')) return '📊'
  return '📁'
}

export default function ChatMediaPanel({
  conversacionId,
  messages,
  onImageClick,
  onPdfClick,
  onClose,
}: ChatMediaPanelProps) {
  const [activeTab, setActiveTab] = useState<MediaTab>('multimedia')
  const [mediaItems, setMediaItems] = useState<Mensaje[]>([])
  const [docItems, setDocItems] = useState<Mensaje[]>([])
  const [loading, setLoading] = useState(false)
  const [mediaTotal, setMediaTotal] = useState(0)
  const [docTotal, setDocTotal] = useState(0)
  const [mediaOffset, setMediaOffset] = useState(0)
  const [docOffset, setDocOffset] = useState(0)
  const [hasMoreMedia, setHasMoreMedia] = useState(true)
  const [hasMoreDocs, setHasMoreDocs] = useState(true)
  const scrollRef = useRef<HTMLDivElement>(null)

  const PAGE_SIZE = 40

  const fetchMedia = useCallback(async (tipo: string, offset: number) => {
    try {
      const token = localStorage.getItem('auth_token')
      const params = new URLSearchParams({
        conversacion_id: String(conversacionId),
        tipo,
        limit: String(PAGE_SIZE),
        offset: String(offset),
      })

      const res = await fetch(`${API_URL}/api/chat/mensajes/media?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (res.ok) {
        return await res.json() as { items: Mensaje[]; total: number }
      }
    } catch { /* silent */ }
    return { items: [], total: 0 }
  }, [conversacionId])

  // Load media & docs on mount
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setMediaItems([])
    setDocItems([])
    setMediaOffset(0)
    setDocOffset(0)

    async function loadAll() {
      const [mediaRes, docRes] = await Promise.all([
        fetchMedia('imagen,video', 0),
        fetchMedia('documento', 0),
      ])

      if (!cancelled) {
        setMediaItems(mediaRes.items)
        setMediaTotal(mediaRes.total)
        setHasMoreMedia(mediaRes.items.length >= PAGE_SIZE)
        setMediaOffset(mediaRes.items.length)

        setDocItems(docRes.items)
        setDocTotal(docRes.total)
        setHasMoreDocs(docRes.items.length >= PAGE_SIZE)
        setDocOffset(docRes.items.length)

        setLoading(false)
      }
    }

    loadAll()
    return () => { cancelled = true }
  }, [conversacionId, fetchMedia])

  const loadMoreMedia = async () => {
    const res = await fetchMedia('imagen,video', mediaOffset)
    setMediaItems(prev => [...prev, ...res.items])
    setMediaOffset(prev => prev + res.items.length)
    setHasMoreMedia(res.items.length >= PAGE_SIZE)
  }

  const loadMoreDocs = async () => {
    const res = await fetchMedia('documento', docOffset)
    setDocItems(prev => [...prev, ...res.items])
    setDocOffset(prev => prev + res.items.length)
    setHasMoreDocs(res.items.length >= PAGE_SIZE)
  }

  // Extract links from already-loaded messages
  const links = extractLinks(messages)
  const totalCount = mediaTotal + docTotal + links.length

  const TABS: { key: MediaTab; label: string; count: number }[] = [
    { key: 'multimedia', label: 'Multimedia', count: mediaTotal },
    { key: 'documentos', label: 'Documentos', count: docTotal },
    { key: 'enlaces', label: 'Enlaces', count: links.length },
  ]

  const mediaGroups = groupByMonth(mediaItems)
  const docGroups = groupByMonth(docItems)
  const linkGroups = groupByMonth(links.map(l => ({ ...l, fecha_mensaje: l.mensaje.fecha_mensaje })))

  return (
    <div className="chat-media-panel">
      {/* Header */}
      <div className="chat-media-panel__header">
        <button
          className="chat-media-panel__back"
          onClick={onClose}
          type="button"
          title="Cerrar"
        >
          <IconChevronLeft size={18} />
        </button>
        <div className="chat-media-panel__title">
          <IconPaperclip size={16} />
          <span>Archivos, enlaces y documentos</span>
          <span className="chat-media-panel__count">{totalCount}</span>
        </div>
        <button
          className="chat-media-panel__close"
          onClick={onClose}
          type="button"
        >
          <IconXCircle size={18} />
        </button>
      </div>

      {/* Tabs */}
      <div className="chat-media-panel__tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`chat-media-tab ${activeTab === tab.key ? 'chat-media-tab--active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
            type="button"
          >
            {tab.label}
            {tab.count > 0 && (
              <span className="chat-media-tab__badge">{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="chat-media-panel__body" ref={scrollRef}>
        {loading ? (
          <div className="chat-media-panel__loading">
            <div className="auth-spinner" style={{ width: 20, height: 20 }} />
            <span>Cargando archivos...</span>
          </div>
        ) : (
          <>
            {/* ─── Multimedia Tab ─── */}
            {activeTab === 'multimedia' && (
              mediaItems.length === 0 ? (
                <div className="chat-media-panel__empty">
                  <IconImage size={32} />
                  <span>No hay fotos ni videos</span>
                </div>
              ) : (
                <>
                  {mediaGroups.map(group => (
                    <div key={group.label} className="chat-media-month-group">
                      <div className="chat-media-month-label">{group.label}</div>
                      <div className="chat-media-grid">
                        {group.items.map(item => (
                          <button
                            key={item.id}
                            className="chat-media-grid__item"
                            onClick={() => {
                              if (item.tipo === 'video') {
                                onImageClick(item.media_url!)
                              } else {
                                onImageClick(item.media_url!)
                              }
                            }}
                            type="button"
                          >
                            {item.tipo === 'video' ? (
                              <div className="chat-media-grid__video-wrap">
                                <video
                                  src={item.media_url!}
                                  preload="metadata"
                                  muted
                                />
                                <div className="chat-media-grid__play">▶</div>
                              </div>
                            ) : (
                              <img
                                src={item.media_url!}
                                alt={item.media_nombre || 'Imagen'}
                                loading="lazy"
                              />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  {hasMoreMedia && (
                    <button
                      className="btn btn-sm btn-secondary chat-media-panel__load-more"
                      onClick={loadMoreMedia}
                      type="button"
                    >
                      Cargar más
                    </button>
                  )}
                </>
              )
            )}

            {/* ─── Documents Tab ─── */}
            {activeTab === 'documentos' && (
              docItems.length === 0 ? (
                <div className="chat-media-panel__empty">
                  <IconFile size={32} />
                  <span>No hay documentos</span>
                </div>
              ) : (
                <>
                  {docGroups.map(group => (
                    <div key={group.label} className="chat-media-month-group">
                      <div className="chat-media-month-label">{group.label}</div>
                      {group.items.map(item => (
                        <div key={item.id} className="chat-media-doc-item">
                          <div className="chat-media-doc-item__icon">
                            {fileIcon(item.media_mime, item.media_nombre)}
                          </div>
                          <div className="chat-media-doc-item__info">
                            <span className="chat-media-doc-item__name">
                              {item.media_nombre || 'Documento'}
                            </span>
                            <span className="chat-media-doc-item__meta">
                              {formatSize(item.media_size)}
                              {item.media_size && ' · '}
                              {formatMediaDate(item.fecha_mensaje)}
                            </span>
                          </div>
                          <div className="chat-media-doc-item__actions">
                            {item.media_mime?.includes('pdf') && (
                              <button
                                className="chat-media-doc-action"
                                onClick={() => onPdfClick(item.media_url!, item.media_nombre || 'Documento.pdf')}
                                type="button"
                                title="Ver PDF"
                              >
                                <IconFile size={14} />
                              </button>
                            )}
                            <a
                              className="chat-media-doc-action"
                              href={item.media_url!}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Descargar"
                            >
                              <IconDownload size={14} />
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                  {hasMoreDocs && (
                    <button
                      className="btn btn-sm btn-secondary chat-media-panel__load-more"
                      onClick={loadMoreDocs}
                      type="button"
                    >
                      Cargar más
                    </button>
                  )}
                </>
              )
            )}

            {/* ─── Links Tab ─── */}
            {activeTab === 'enlaces' && (
              links.length === 0 ? (
                <div className="chat-media-panel__empty">
                  <IconExternalLink size={32} />
                  <span>No hay enlaces compartidos</span>
                </div>
              ) : (
                linkGroups.map(group => (
                  <div key={group.label} className="chat-media-month-group">
                    <div className="chat-media-month-label">{group.label}</div>
                    {group.items.map((item, idx) => (
                      <a
                        key={`${item.url}-${idx}`}
                        className="chat-media-link-item"
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <div className="chat-media-link-item__icon">
                          <IconExternalLink size={16} />
                        </div>
                        <div className="chat-media-link-item__info">
                          <span className="chat-media-link-item__url">
                            {item.url.length > 60 ? item.url.slice(0, 60) + '...' : item.url}
                          </span>
                          <span className="chat-media-link-item__date">
                            {formatMediaDate(item.fecha_mensaje)}
                          </span>
                        </div>
                      </a>
                    ))}
                  </div>
                ))
              )
            )}
          </>
        )}
      </div>
    </div>
  )
}
